import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { communityTrpc } from '@org/clients/community';
import { Input, Avatar, AvatarFallback, AvatarImage } from '@org/ui';
import { Search, FileText, User, Users, X, Loader2 } from 'lucide-react';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslation } from 'react-i18next';

export function SearchBar() {
  const { t } = useTranslation('community');
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = communityTrpc.search.global.useQuery(
    { query: debouncedQuery, limit: 5 },
    { enabled: debouncedQuery.length >= 2 }
  );

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate({ to: '/search', search: { q: query.trim() } });
      setIsOpen(false);
    }
  };

  const hasResults =
    data &&
    (data.posts.length > 0 || data.users.length > 0 || data.spaces.length > 0);

  return (
    <div ref={containerRef} className="relative hidden w-full max-w-xl lg:block">
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="search"
            placeholder={t('search.placeholder')}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            className="pl-9 pr-9"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>

      {/* Results Dropdown */}
      {isOpen && query.length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-2xl border border-border bg-card shadow-lg">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !hasResults ? (
            <div className="py-8 text-center text-muted-foreground">
              <Search className="mx-auto h-8 w-8 opacity-50" />
              <p className="mt-2 text-sm">{t('search.noResults', { query })}</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {/* Posts */}
              {data.posts.length > 0 && (
                <div className="border-b p-2">
                  <p className="px-2 py-1 text-xs font-medium uppercase text-muted-foreground">
                    {t('search.tabPosts')}
                  </p>
                  {data.posts.map((post) => (
                    <Link
                      key={post.postId}
                      to="/posts/$postId"
                      params={{ postId: post.postId }}
                      onClick={() => setIsOpen(false)}
                      className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                    >
                      <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0">
                        <p className="truncate font-medium">{post.title}</p>
                        <p className="truncate text-sm text-muted-foreground">
                          {t('search.by', { name: post.author?.displayName || post.author?.firstName })}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Users */}
              {data.users.length > 0 && (
                <div className="border-b p-2">
                  <p className="px-2 py-1 text-xs font-medium uppercase text-muted-foreground">
                    {t('search.tabPeople')}
                  </p>
                  {data.users.map((user) => (
                    <Link
                      key={user.userId}
                      to="/profile/$userId"
                      params={{ userId: user.userId }}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.profileImageUrl ?? undefined} />
                        <AvatarFallback>
                          {(user.displayName || user.firstName || 'U')[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {user.displayName ||
                            [user.firstName, user.lastName]
                              .filter(Boolean)
                              .join(' ')}
                        </p>
                        {user.bio && (
                          <p className="truncate text-sm text-muted-foreground">
                            {user.bio}
                          </p>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* Spaces */}
              {data.spaces.length > 0 && (
                <div className="p-2">
                  <p className="px-2 py-1 text-xs font-medium uppercase text-muted-foreground">
                    {t('search.tabSpaces')}
                  </p>
                  {data.spaces.map((space) => (
                    <Link
                      key={space.spaceId}
                      to="/spaces/$spaceId"
                      params={{ spaceId: space.spaceId }}
                      onClick={() => setIsOpen(false)}
                      className="flex items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                    >
                      {space.iconUrl ? (
                        <img
                          src={space.iconUrl}
                          alt={space.name}
                          className="h-8 w-8 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                          <Users className="h-4 w-4 text-primary" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate font-medium">{space.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {space.subscriberCount} {t('search.members')}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}

              {/* View All Link */}
              <div className="border-t p-2">
                <Link
                  to="/search"
                  search={{ q: query }}
                  onClick={() => setIsOpen(false)}
                  className="block rounded-md bg-muted/50 py-2 text-center text-sm font-medium text-primary hover:bg-muted"
                >
                  {t('search.viewAllResults', { query })}
                </Link>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
